import React, { useEffect, useMemo, useRef } from 'react';
import classnames from 'classnames';
import { ConfigProvider, Layout, StatusTip } from 'tea-component';
import LeftMenu from '../components/LeftMenu';
import { getLocale, useSelector, useLocation } from 'umi';
import { getTeaLocale } from '@/pages/home/utils/lang';
import { TGameInfoType } from '../components/FilterCommon/types';
import { removeFilterCondStorage } from '../components/FilterCommon/utils/storage';
import { FilterPageSourceContext } from '@op/components/ContentBody/context';
import useUrlLinkUpdate from '@op/hooks/useUrlLinkUpdate';
import { useRegionCountryDict } from '@op/hooks/useRegionCountryDict';
import { isOpinionRouteMatched } from '../_config/utils';
import NotFound from '../pages/NotFound/index';
import '@op/assets/scss/index.scss';
import { GAME_LIST_URL } from '@op/constants/page';
import { useEmbeddedPageOptions } from '@home/hooks/useEmbeddedPageOptions';
const { Body, Content } = Layout;

export function hideSidebarByPage() {
  const whites = [
    '/opinion/download',
    '/opinion/Summary/Excel',
    '/opinion/NewDownload',
    '/AITaskCenter/Unsubscribe',
  ];
  return whites.some((item) => location.pathname.includes(item));
}

export default function Layouts(props: any) {
  const locale = getTeaLocale(getLocale());
  const location = useLocation();
  const embedOptions = useEmbeddedPageOptions();
  const localGameInfo = useRef<TGameInfoType | null>(null);
  const {
    excludeSource,
    hidePageIds,
    pageLoading,
    gameInfo,
    listMenuLoading,
    wholeGameInfo,
    listMenu,
    hideGameListMenu
  } = useSelector((state: any) => {
    const {
      opinionCommon: {
        pageLoading,
        gameInfo,
        listMenuLoading,
        wholeGameInfo,
        listMenu,
        excludeSource,
        hidePageIds,
        isExpand,
        hideGameListMenu
      },
    } = state;
    return {
      pageLoading,
      gameInfo,
      listMenuLoading,
      wholeGameInfo,
      listMenu,
      excludeSource,
      hidePageIds,
      isExpand,
      hideGameListMenu
    };
  });

  useUrlLinkUpdate({ gameInfo, wholeGameInfo, listMenu });
  // 布局顶层注入 region_country 字典，兜底直达/刷新场景下 store 未就绪的问题。
  // 切游戏主路径已在 changeStoreGameInfo 预取并写入 store，此处命中缓存、开销极小。
  useRegionCountryDict(gameInfo);
  // 游戏切换清理cond数据
  useEffect(() => {
    if (
      localGameInfo.current?.edition_unified_id !== gameInfo?.edition_unified_id
    ) {
      if (localGameInfo.current !== null)
        removeFilterCondStorage(localGameInfo.current);
      localGameInfo.current = gameInfo;
    }
  }, [gameInfo]);
  // 刷新等操作清理cond数据
  useEffect(() => {
    function unload() {
      if (localGameInfo.current !== null) {
        removeFilterCondStorage(localGameInfo.current);
        // TODO: newOpinionGameList 缓存已停用，此处 removeItem 已移除
        sessionStorage.removeItem('newOpinionGameList');
        localStorage.removeItem('OpinionGameInfo');
      }
    }
    window.addEventListener('unload', unload);

    return () => {
      window.removeEventListener('unload', unload);
    };
  }, []);

  const pageSourceContent = useMemo(() => {
    return {
      excludeSource,
      hidePageIds,
    };
  }, [excludeSource, hidePageIds]);

  const renderContent = useMemo(() => {
    if (pageLoading || listMenuLoading) {
      return (
        <div
          style={{
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <StatusTip status="loading" />
        </div>
      );
    }
    if (hideGameListMenu && location.pathname === GAME_LIST_URL) {
      return <NotFound />;
    }
    if (!isOpinionRouteMatched(location.pathname)) {
      return <NotFound />;
    }
    return props.children;
  }, [pageLoading, listMenuLoading, hideGameListMenu, location.pathname]);

  return (
    <FilterPageSourceContext.Provider value={pageSourceContent}>
      <ConfigProvider locale={locale}>
        <Body
          className={classnames('layout__body', {
            'dbh-embed-hide-page-header': embedOptions.hidePageHeader,
            'dbh-embed-hide-page-tabs': embedOptions.hidePageTabs,
          })}
        >
          {!embedOptions.hideLeftMenu && !hideSidebarByPage() && (
            <LeftMenu
              loading={listMenuLoading}
              listMenu={listMenu}
              gameInfo={gameInfo}
              wholeGameInfo={wholeGameInfo}
              hideGameListMenu={hideGameListMenu}
            />
          )}

          <Content className="dbo-body">{renderContent}</Content>
        </Body>
      </ConfigProvider>
    </FilterPageSourceContext.Provider>
  );
}
